import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiService } from '../services/api';
import { HierarchyNode } from '../types';
import { FolderCard } from '../components/FolderCard';

const transformNodes = (nodes: HierarchyNode[]): HierarchyNode[] => {
  return nodes.filter(node => node.type === 'category');
};

const CategoriesPage: React.FC = () => {
  const [categories, setCategories] = useState<HierarchyNode[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await apiService.getHierarchyTree();
        if (response.success && response.data) {
          setCategories(transformNodes(response.data));
        } else {
          setError('Failed to load categories');
        }
      } catch (err: any) {
        console.error('Failed to fetch categories for public page:', err);
        setError(err.message || 'Failed to load categories');
      } finally {
        setLoading(false);
      }
    };

    load();
  }, []);

  const renderNodes = (nodes: HierarchyNode[], depth = 0) => {
    return nodes.map(node => {
      if (node.type !== 'category') {
        return null;
      }
      const link = `/categories/${encodeURIComponent(node.category?.name || node.name)}`;

      return (
        <div key={node.id} style={{ paddingLeft: depth * 20 }} className="mb-4">
          <Link to={link} className="block">
            <FolderCard
              category={node.category!}
              tutorialCount={node.tutorialCount || 0}
              onClick={() => {}}
            />
          </Link>
          {node.children && node.children.length > 0 && (
            <div className="mt-2">
              {renderNodes(node.children, depth + 1)}
            </div>
          )}
        </div>
      );
    });
  };

  return (
    <div className="min-h-screen bg-gray-50 py-10">
      <div className="max-w-6xl mx-auto px-4">
        <div className="mb-8">
          <h1 className="text-3xl font-semibold text-gray-900">Browse Categories</h1>
          <p className="text-gray-600 mt-1">
            Click any category or subcategory to open its tutorial list.
          </p>
        </div>

        {loading && <div className="text-center text-gray-600">Loading categories...</div>}
        {error && (
          <div className="text-center text-red-600">
            <p>{error}</p>
          </div>
        )}

        {!loading && !error && categories.length === 0 && (
          <div className="text-center text-gray-500">No categories available.</div>
        )}

        {!loading && !error && categories.length > 0 && (
          <div className="space-y-6">
            {renderNodes(categories)}
          </div>
        )}
      </div>
    </div>
  );
};

export default CategoriesPage;

